import { useEffect, useState } from "react";
import api, { apiError } from "../../lib/api";
import { Save, Plus, X } from "lucide-react";
import { toast } from "sonner";
import ImageUploader from "../../components/admin/ImageUploader";

export default function AdminContent() {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("about");

  const load = async () => {
    try {
      const { data } = await api.get("/admin/content");
      setContent(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const update = (section, key, value) =>
    setContent((c) => ({ ...c, [section]: { ...(c[section] || {}), [key]: value } }));

  const save = async (sectionKey) => {
    setSaving(true);
    try {
      await api.put("/admin/content", { [sectionKey]: content[sectionKey] });
      toast.success("Saved");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading || !content) return <div className="p-10 font-script text-3xl text-blush-600">loading…</div>;

  const TABS = [
    { id: "home", label: "Home / Hero" },
    { id: "about", label: "About" },
    { id: "contact", label: "Contact" },
    { id: "footer", label: "Footer" },
    { id: "social", label: "Social" },
    { id: "seo", label: "SEO" },
    { id: "fabric_care", label: "Fabric & Care" },
  ];

  return (
    <div className="p-6 md:p-10 max-w-5xl" data-testid="admin-content">
      <header className="mb-8">
        <span className="font-script text-3xl text-blush-400">the words</span>
        <h1 className="font-heading text-3xl md:text-4xl text-ink-900">Website content</h1>
        <p className="mt-2 font-body text-ink-500">Edit copy, images, links, and SEO that appear on the storefront.</p>
      </header>

      <div className="flex gap-2 mb-8 border-b border-blush-100 overflow-x-auto">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} className={`px-5 py-3 font-body text-sm uppercase tracking-[0.18em] border-b-2 transition whitespace-nowrap ${
            tab === t.id ? "border-blush-600 text-blush-600" : "border-transparent text-ink-700 hover:text-blush-600"
          }`}>{t.label}</button>
        ))}
      </div>

      {tab === "home" && <HomeEditor content={content.home || {}} update={(k, v) => update("home", k, v)} onSave={() => save("home")} saving={saving} />}
      {tab === "about" && <AboutEditor content={content.about || {}} update={(k, v) => update("about", k, v)} onSave={() => save("about")} saving={saving} />}
      {tab === "contact" && <ContactEditor content={content.contact || {}} update={(k, v) => update("contact", k, v)} onSave={() => save("contact")} saving={saving} />}
      {tab === "footer" && <FooterEditor content={content.footer || {}} update={(k, v) => update("footer", k, v)} onSave={() => save("footer")} saving={saving} />}
      {tab === "social" && <SocialEditor content={content.social || {}} update={(k, v) => update("social", k, v)} onSave={() => save("social")} saving={saving} />}
      {tab === "seo" && <SEOEditor content={content.seo || {}} update={(k, v) => update("seo", k, v)} onSave={() => save("seo")} saving={saving} />}
      {tab === "fabric_care" && <FabricCareEditor content={content.fabric_care || {}} update={(k, v) => update("fabric_care", k, v)} onSave={() => save("fabric_care")} saving={saving} />}
    </div>
  );
}

function Section({ title, onSave, saving, children }) {
  return (
    <section className="rounded-3xl bg-white border border-blush-100 p-6 md:p-8">
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-heading text-xl md:text-2xl text-ink-900">{title}</h2>
        <button onClick={onSave} disabled={saving} data-testid="content-save-btn" className="btn-maroon text-xs inline-flex items-center gap-2 disabled:opacity-60">
          <Save size={12} strokeWidth={1.6} /> {saving ? "Saving…" : "Save"}
        </button>
      </div>
      <div className="grid gap-4">{children}</div>
    </section>
  );
}

function Txt({ label, value, onChange, textarea, rows = 3 }) {
  return (
    <div>
      <label className="text-xs font-body uppercase tracking-[0.2em] text-ink-500">{label}</label>
      {textarea ? (
        <textarea value={value ?? ""} onChange={(e) => onChange(e.target.value)} rows={rows} className="field-area mt-2" />
      ) : (
        <input value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="field mt-2" />
      )}
    </div>
  );
}

function HomeEditor({ content, update, onSave, saving }) {
  return (
    <Section title="Home / Hero section" onSave={onSave} saving={saving}>
      <Txt label="Eyebrow (small label above title)" value={content.hero_eyebrow} onChange={(v) => update("hero_eyebrow", v)} />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Txt label="Title — line 1" value={content.hero_title_lead} onChange={(v) => update("hero_title_lead", v)} />
        <Txt label="Title — accent (script italic)" value={content.hero_title_accent} onChange={(v) => update("hero_title_accent", v)} />
        <Txt label="Title — tail" value={content.hero_title_tail} onChange={(v) => update("hero_title_tail", v)} />
      </div>
      <Txt label="Hero paragraph" value={content.hero_paragraph} onChange={(v) => update("hero_paragraph", v)} textarea />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Txt label="CTA primary label" value={content.hero_cta_primary} onChange={(v) => update("hero_cta_primary", v)} />
        <Txt label="CTA secondary label" value={content.hero_cta_secondary} onChange={(v) => update("hero_cta_secondary", v)} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="text-xs font-body uppercase tracking-[0.2em] text-ink-500">Main image (desktop & mobile)</label>
          <div className="mt-2"><ImageUploader value={content.hero_image_main} onChange={(v) => update("hero_image_main", v)} onRemove={() => update("hero_image_main", "")} /></div>
        </div>
        <div>
          <label className="text-xs font-body uppercase tracking-[0.2em] text-ink-500">Secondary image (desktop)</label>
          <div className="mt-2"><ImageUploader value={content.hero_image_secondary} onChange={(v) => update("hero_image_secondary", v)} onRemove={() => update("hero_image_secondary", "")} /></div>
        </div>
        <div>
          <label className="text-xs font-body uppercase tracking-[0.2em] text-ink-500">Tertiary image (desktop)</label>
          <div className="mt-2"><ImageUploader value={content.hero_image_tertiary} onChange={(v) => update("hero_image_tertiary", v)} onRemove={() => update("hero_image_tertiary", "")} /></div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Txt label="Floating badge — title" value={content.hero_badge_title} onChange={(v) => update("hero_badge_title", v)} />
        <Txt label="Floating badge — subtitle" value={content.hero_badge_sub} onChange={(v) => update("hero_badge_sub", v)} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="grid grid-cols-2 gap-2">
          <Txt label="Stat 1 value" value={content.stat_1_value} onChange={(v) => update("stat_1_value", v)} />
          <Txt label="Stat 1 label" value={content.stat_1_label} onChange={(v) => update("stat_1_label", v)} />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Txt label="Stat 2 value" value={content.stat_2_value} onChange={(v) => update("stat_2_value", v)} />
          <Txt label="Stat 2 label" value={content.stat_2_label} onChange={(v) => update("stat_2_label", v)} />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Txt label="Stat 3 value" value={content.stat_3_value} onChange={(v) => update("stat_3_value", v)} />
          <Txt label="Stat 3 label" value={content.stat_3_label} onChange={(v) => update("stat_3_label", v)} />
        </div>
      </div>
    </Section>
  );
}

function AboutEditor({ content, update, onSave, saving }) {
  const values = content.values || [];
  const setValues = (arr) => update("values", arr);
  const paragraphs = content.story_paragraphs || [];

  return (
    <Section title="About page" onSave={onSave} saving={saving}>
      <Txt label="Hero eyebrow" value={content.hero_eyebrow} onChange={(v) => update("hero_eyebrow", v)} />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Txt label="Hero title lead" value={content.hero_title_lead} onChange={(v) => update("hero_title_lead", v)} />
        <Txt label="Hero title accent (italic)" value={content.hero_title_accent} onChange={(v) => update("hero_title_accent", v)} />
      </div>
      <Txt label="Hero paragraph" value={content.hero_paragraph} onChange={(v) => update("hero_paragraph", v)} textarea />
      <Txt label="Story eyebrow" value={content.story_eyebrow} onChange={(v) => update("story_eyebrow", v)} />
      <Txt label="Story title" value={content.story_title} onChange={(v) => update("story_title", v)} />
      <div>
        <label className="text-xs font-body uppercase tracking-[0.2em] text-ink-500">Story paragraphs</label>
        {paragraphs.map((p, i) => (
          <div key={i} className="mt-2 flex items-start gap-2">
            <textarea value={p} onChange={(e) => update("story_paragraphs", paragraphs.map((x, idx) => idx === i ? e.target.value : x))} className="field-area flex-1" rows={3} />
            <button type="button" onClick={() => update("story_paragraphs", paragraphs.filter((_, idx) => idx !== i))} className="h-9 w-9 grid place-items-center rounded-full text-blush-600 hover:bg-blush-50"><X size={14} strokeWidth={1.6} /></button>
          </div>
        ))}
        <button type="button" onClick={() => update("story_paragraphs", [...paragraphs, ""])} className="mt-2 text-xs uppercase tracking-[0.2em] text-blush-600 inline-flex items-center gap-1">
          <Plus size={12} strokeWidth={1.6} /> Add paragraph
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="text-xs font-body uppercase tracking-[0.2em] text-ink-500">Image — left</label>
          <div className="mt-2"><ImageUploader value={content.image_left} onChange={(v) => update("image_left", v)} onRemove={() => update("image_left", "")} /></div>
        </div>
        <div>
          <label className="text-xs font-body uppercase tracking-[0.2em] text-ink-500">Image — right</label>
          <div className="mt-2"><ImageUploader value={content.image_right} onChange={(v) => update("image_right", v)} onRemove={() => update("image_right", "")} /></div>
        </div>
      </div>
      <div>
        <label className="text-xs font-body uppercase tracking-[0.2em] text-ink-500">Values (3 recommended)</label>
        <div className="grid gap-3 mt-2">
          {values.map((v, i) => (
            <div key={i} className="rounded-2xl bg-cream-100 border border-blush-100 p-4">
              <div className="flex items-start justify-between gap-2">
                <input placeholder="Title" value={v.title || ""} onChange={(e) => setValues(values.map((x, idx) => idx === i ? { ...x, title: e.target.value } : x))} className="field flex-1 !py-2" />
                <button type="button" onClick={() => setValues(values.filter((_, idx) => idx !== i))} className="h-9 w-9 grid place-items-center rounded-full text-blush-600"><X size={14} strokeWidth={1.6} /></button>
              </div>
              <textarea placeholder="Body" value={v.body || ""} onChange={(e) => setValues(values.map((x, idx) => idx === i ? { ...x, body: e.target.value } : x))} className="field-area mt-2" rows={2} />
            </div>
          ))}
          <button type="button" onClick={() => setValues([...values, { title: "", body: "" }])} className="text-xs uppercase tracking-[0.2em] text-blush-600 inline-flex items-center gap-1">
            <Plus size={12} strokeWidth={1.6} /> Add value
          </button>
        </div>
      </div>
    </Section>
  );
}

function ContactEditor({ content, update, onSave, saving }) {
  return (
    <Section title="Contact page" onSave={onSave} saving={saving}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Txt label="Email" value={content.email} onChange={(v) => update("email", v)} />
        <Txt label="Email subtitle" value={content.email_sub} onChange={(v) => update("email_sub", v)} />
        <Txt label="Phone" value={content.phone} onChange={(v) => update("phone", v)} />
        <Txt label="Phone subtitle" value={content.phone_sub} onChange={(v) => update("phone_sub", v)} />
        <Txt label="Address" value={content.address} onChange={(v) => update("address", v)} />
        <Txt label="Address subtitle" value={content.address_sub} onChange={(v) => update("address_sub", v)} />
      </div>
    </Section>
  );
}

function FooterEditor({ content, update, onSave, saving }) {
  return (
    <Section title="Footer" onSave={onSave} saving={saving}>
      <Txt label="Brand tagline" value={content.tagline} onChange={(v) => update("tagline", v)} textarea />
      <Txt label="Contact block (newline-separated)" value={content.contact_block} onChange={(v) => update("contact_block", v)} textarea rows={4} />
      <Txt label="Copyright (use {year})" value={content.copyright} onChange={(v) => update("copyright", v)} />
      <Txt label="Sub-copyright" value={content.sub_copyright} onChange={(v) => update("sub_copyright", v)} />
    </Section>
  );
}

function SocialEditor({ content, update, onSave, saving }) {
  return (
    <Section title="Social links" onSave={onSave} saving={saving}>
      <Txt label="Instagram URL" value={content.instagram} onChange={(v) => update("instagram", v)} />
      <Txt label="Facebook URL" value={content.facebook} onChange={(v) => update("facebook", v)} />
      <Txt label="Email URL (mailto)" value={content.email_url} onChange={(v) => update("email_url", v)} />
    </Section>
  );
}

function SEOEditor({ content, update, onSave, saving }) {
  return (
    <Section title="SEO" onSave={onSave} saving={saving}>
      <Txt label="Site title" value={content.site_title} onChange={(v) => update("site_title", v)} />
      <Txt label="Meta description" value={content.site_description} onChange={(v) => update("site_description", v)} textarea />
      <Txt label="Meta keywords (comma-separated)" value={content.keywords} onChange={(v) => update("keywords", v)} />
      <div>
        <label className="text-xs font-body uppercase tracking-[0.2em] text-ink-500">Open Graph image</label>
        <div className="mt-2 max-w-xs"><ImageUploader value={content.og_image} onChange={(v) => update("og_image", v)} onRemove={() => update("og_image", "")} /></div>
      </div>
    </Section>
  );
}


function FabricCareEditor({ content, update, onSave, saving }) {
  const bullets = Array.isArray(content.care_bullets) ? content.care_bullets : [];
  const setBullet = (i, v) => {
    const next = [...bullets];
    next[i] = v;
    update("care_bullets", next);
  };
  const addBullet = () => update("care_bullets", [...bullets, ""]);
  const removeBullet = (i) => update("care_bullets", bullets.filter((_, idx) => idx !== i));

  return (
    <Section title="Fabric & Care section" onSave={onSave} saving={saving}>
      <label className="flex items-center gap-2 font-body text-sm text-ink-700 cursor-pointer">
        <input
          type="checkbox"
          checked={content.enabled !== false}
          onChange={(e) => update("enabled", e.target.checked)}
          data-testid="fabric-care-enabled"
        />
        Show "Fabric & Care" tab on every product page
      </label>

      <Txt label="Tab title" value={content.title} onChange={(v) => update("title", v)} placeholder="Fabric & Care" />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Txt label="Fabric heading" value={content.fabric_heading} onChange={(v) => update("fabric_heading", v)} />
        <Txt label="Care heading" value={content.care_heading} onChange={(v) => update("care_heading", v)} />
      </div>
      <Txt label="Fabric description" value={content.fabric_body} onChange={(v) => update("fabric_body", v)} textarea />

      <div>
        <label className="text-xs font-body uppercase tracking-[0.2em] text-ink-500">Care bullets</label>
        <div className="mt-2 space-y-2">
          {bullets.map((b, i) => (
            <div key={i} className="flex items-center gap-2">
              <input
                value={b}
                onChange={(e) => setBullet(i, e.target.value)}
                className="field flex-1"
                data-testid={`care-bullet-${i}`}
              />
              <button
                type="button"
                onClick={() => removeBullet(i)}
                aria-label="Remove bullet"
                className="h-9 w-9 grid place-items-center rounded-full text-blush-600 hover:bg-blush-50"
              >
                ✕
              </button>
            </div>
          ))}
          <button type="button" onClick={addBullet} data-testid="care-bullet-add" className="btn-ghost-rose text-xs">
            + Add bullet
          </button>
        </div>
      </div>
    </Section>
  );
}
